// src/auth/RegisterPage.tsx

import { useState } from "react";
import { useNotify, useLogin, useTranslate } from "react-admin";
import {
  TextField,
  Button,
  Typography,
  Box,
  Link as MuiLink,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { usePageTitle } from "@/utilities";
import { signupUser } from "@/services/auth/signupUser";
import PasswordField from "@/components/passwordfield/PasswordField";

import useStyles from "./style";
import { MadagascarRegion, UserGender } from "@/types/auth";

const genderOptions = ["H", "F", "JE_NE_VEUX_PAS"] as const;

const regionOptions = [
  "ANTANANARIVO",
  "ANTSIRANANA",
  "FIANARANTSOA",
  "MAHAJANGA",
  "TOAMASINA",
  "TOLIARA",
] as const;

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;

  try {
    return JSON.stringify(err);
  } catch {
    return "Échec de l'inscription";
  }
}

export default function RegisterPage() {
  const classes = useStyles();

  const notify = useNotify();
  const navigate = useNavigate();
  const translate = useTranslate();
  const login = useLogin();

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    pseudo: "",
    full_name: "",
    email: "",
    password: "",
    confirm: "",

    locality: "",
    birth_year: "",
    gender: "",
    region: "",
  });

  usePageTitle(translate("ra.gameoo.register.title"));

  const onChange =
    (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((previous) => ({
        ...previous,
        [key]: e.target.value,
      }));
    };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.pseudo || !form.full_name || !form.email || !form.password) {
      notify(translate("ra.gameoo.register.errorMessage"), {
        type: "warning",
      });

      return;
    }

    if (form.password !== form.confirm) {
      notify(translate("ra.gameoo.register.confirmPasswordError"), {
        type: "warning",
      });

      return;
    }

    const currentYear = new Date().getFullYear();

    if (
      form.birth_year &&
      (Number(form.birth_year) < 1900 || Number(form.birth_year) > currentYear)
    ) {
      notify(translate("ra.gameoo.register.invalidBirthYear"), {
        type: "warning",
      });

      return;
    }

    setLoading(true);

    try {
      await signupUser({
        pseudo: form.pseudo.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,

        locality: form.locality.trim() || undefined,
        birth_year: form.birth_year ? Number(form.birth_year) : undefined,

        gender: form.gender ? (form.gender as UserGender) : undefined,

        region: form.region ? (form.region as MadagascarRegion) : undefined,
      });

      await login(
        {
          username: form.pseudo,
          password: form.password,
        },
        "/",
      );
    } catch (err: unknown) {
      notify(getErrorMessage(err), {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.form}>
        <Box
          className={classes.logo}
          component="img"
          src="/logo-gameoo.png"
          alt="GAMEOO"
        />

        <Typography className={classes.title} variant="h5" align="left">
          {translate("ra.gameoo.register.title")}
        </Typography>

        <form onSubmit={onSubmit}>
          {/* Pseudo */}
          <TextField
            label={translate("ra.gameoo.register.pseudoLabel")}
            fullWidth
            required
            sx={{ mb: 2 }}
            value={form.pseudo}
            onChange={onChange("pseudo")}
            disabled={loading}
          />

          {/* Full name */}
          <TextField
            label={translate("ra.gameoo.register.fullNameLabel")}
            fullWidth
            required
            sx={{ mb: 2 }}
            value={form.full_name}
            onChange={onChange("full_name")}
            disabled={loading}
          />

          {/* Locality */}
          <TextField
            label={translate("ra.gameoo.register.localityLabel")}
            fullWidth
            sx={{ mb: 2 }}
            value={form.locality}
            onChange={onChange("locality")}
            disabled={loading}
          />

          {/* Birth year */}
          <TextField
            label={translate("ra.gameoo.register.birthYearLabel")}
            type="number"
            fullWidth
            sx={{ mb: 2 }}
            value={form.birth_year}
            onChange={onChange("birth_year")}
            disabled={loading}
            slotProps={{
              htmlInput: {
                min: 1900,
                max: new Date().getFullYear(),
              },
            }}
          />

          {/* Gender */}
          <TextField
            select
            label={translate("ra.gameoo.register.genderLabel")}
            fullWidth
            sx={{ mb: 2 }}
            value={form.gender}
            onChange={onChange("gender")}
            disabled={loading}
          >
            {genderOptions.map((gender) => (
              <MenuItem key={gender} value={gender}>
                {translate(`ra.gameoo.register.gender.${gender}`)}
              </MenuItem>
            ))}
          </TextField>

          {/* Region */}
          <TextField
            select
            label={translate("ra.gameoo.register.regionLabel")}
            fullWidth
            sx={{ mb: 2 }}
            value={form.region}
            onChange={onChange("region")}
            disabled={loading}
          >
            {regionOptions.map((region) => (
              <MenuItem key={region} value={region}>
                {translate(`ra.gameoo.register.regions.${region}`)}
              </MenuItem>
            ))}
          </TextField>

          {/* Email */}
          <TextField
            label={translate("ra.gameoo.register.emailLabel")}
            type="email"
            fullWidth
            required
            sx={{ mb: 2 }}
            value={form.email}
            onChange={onChange("email")}
            disabled={loading}
            autoComplete="email"
          />

          {/* Password */}
          <PasswordField
            label={translate("ra.gameoo.register.passwordLabel")}
            fullWidth
            required
            sx={{ mb: 2 }}
            value={form.password}
            onChange={onChange("password")}
            disabled={loading}
            autoComplete="new-password"
          />

          {/* Confirm password */}
          <PasswordField
            label={translate("ra.gameoo.register.confirmPasswordLabel")}
            fullWidth
            required
            sx={{ mb: 2 }}
            value={form.confirm}
            onChange={onChange("confirm")}
            disabled={loading}
            autoComplete="new-password"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading
              ? translate("ra.gameoo.register.creatingAccount")
              : translate("ra.gameoo.register.submitButtonLabel")}
          </Button>

          <Typography variant="body2" sx={{ mt: 2 }}>
            {translate("ra.gameoo.register.alreadyHaveAccount")}{" "}
            <MuiLink
              component="button"
              type="button"
              onClick={() => navigate("/login")}
            >
              {translate("ra.gameoo.register.signInLinkLabel")}
            </MuiLink>
          </Typography>
        </form>
      </div>
    </div>
  );
}
